import {
  getCurrentUser,
  updateUserPlan,
  updateUserProfile,
  refreshCurrentUser,
  updateUserPassword,
  deleteUser,
  logout,
} from '../auth.js';
import { PLANS, DEFAULT_PLAN_ID, getPlanById } from '../plans.js';
import { createCheckoutSession, createSubscription, updatePaymentMethod, getReleaseNotes } from '../api.js';
import { getProfileCopy, getCommonCopy, getPlansCopy, getLanguage } from '../i18n.js';
import { showToast } from '../utils/toast.js';
import { renderBillingView } from './BillingView.js';
import { renderExportTemplatesView } from './ExportTemplatesView.js';
import { prepareReleaseNotes } from '../utils/releaseNotes.js';

const html = String.raw;

const formatTemplate = (template, replacements = {}) => {
  if (!template) return '';
  return template.replace(/{{(.*?)}}/g, (_, key) => {
    const value = replacements[key.trim()];
    return value === undefined || value === null ? '' : value;
  });
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatDate = (value, language) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return date.toLocaleDateString(language || 'de', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return date.toISOString().split('T')[0];
  }
};

const renderReleaseNotesList = (items, language, copy) => {
  const prepared = Array.isArray(items) && items[0]?.__prepared ? items : prepareReleaseNotes(items, language);
  return prepared
    .map((item) => {
      const title = escapeHtml(item.title || copy.releaseNotes?.fallbackTitle || '');
      const dateLabel = formatDate(item.createdAt, language);
      const bulletItems = Array.isArray(item.items) ? item.items : [];
      const bulletMarkup = bulletItems.map((entry) => `<li>${escapeHtml(entry)}</li>`).join('');
      return html`
        <article class="release-note">
          <header class="release-note-header">
            <strong>${title}</strong>
            ${dateLabel ? `<span class="muted tiny">${dateLabel}</span>` : ''}
          </header>
          ${bulletMarkup ? `<ul>${bulletMarkup}</ul>` : ''}
        </article>
      `;
    })
    .join('');
};

const config = window.__ANFORDERUNGSAI_CONFIG__ ?? {};

const BILLING_ACTIVE_STATUSES = new Set(['active', 'trialing', 'paid', 'succeeded', 'complete', 'completed']);

const requiresBilling = (plan) => Boolean(plan?.requiresBilling);

const hasActiveBilling = (user) => {
  if (!user) return false;
  const status = typeof user.billingStatus === 'string' ? user.billingStatus.toLowerCase() : '';
  if (BILLING_ACTIVE_STATUSES.has(status)) {
    return true;
  }
  if (user.billing && typeof user.billing === 'object') {
    return Boolean(user.billing.method);
  }
  return false;
};

/**
 * Show Stripe Payment Modal with inline Payment Element
 */
const showPaymentModal = async (planId, onSuccess) => {
  const copy = getProfileCopy();
  const plan = getPlanById(planId);

  if (!plan) {
    throw new Error('Plan not found');
  }

  // Create modal overlay
  const modalOverlay = document.createElement('div');
  modalOverlay.className = 'payment-modal-overlay';
  modalOverlay.innerHTML = html`
    <div class="payment-modal">
      <div class="payment-modal-header">
        <h3>${copy.payment?.title || `Zahlung für ${plan.name}`}</h3>
        <button class="payment-modal-close" aria-label="Schließen">×</button>
      </div>
      <div class="payment-modal-body">
        <div class="payment-plan-summary">
          <div class="pill">${plan.name}</div>
          <span class="price">${plan.priceLabel}</span>
          <p class="muted small">${plan.description}</p>
        </div>
        <div id="payment-element-container"></div>
        <div id="payment-error" class="payment-error" style="display:none;"></div>
      </div>
      <div class="payment-modal-footer">
        <button type="button" class="button-secondary payment-cancel-btn">Abbrechen</button>
        <button type="button" class="button-primary payment-submit-btn">Zahlungsmethode speichern</button>
      </div>
    </div>
  `;

  document.body.appendChild(modalOverlay);

  const closeModal = () => {
    modalOverlay.remove();
  };

  const errorEl = modalOverlay.querySelector('#payment-error');
  const submitBtn = modalOverlay.querySelector('.payment-submit-btn');
  const cancelBtn = modalOverlay.querySelector('.payment-cancel-btn');
  const closeBtn = modalOverlay.querySelector('.payment-modal-close');

  const showError = (message) => {
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    }
  };

  const hideError = () => {
    if (errorEl) {
      errorEl.style.display = 'none';
    }
  };

  // Close handlers
  cancelBtn?.addEventListener('click', closeModal);
  closeBtn?.addEventListener('click', closeModal);
  modalOverlay.addEventListener('click', (e) => {
    if (e.target === modalOverlay) {
      closeModal();
    }
  });

  try {
    // Initialize Stripe
    const stripePublishableKey = config.stripePublishableKey;
    if (!stripePublishableKey || stripePublishableKey === 'YOUR_STRIPE_PUBLISHABLE_KEY_HERE') {
      throw new Error('Stripe Publishable Key nicht konfiguriert');
    }

    if (!window.Stripe) {
      throw new Error('Stripe.js nicht geladen');
    }

    const stripe = window.Stripe(stripePublishableKey);

    // Create subscription and get client secret
    submitBtn.disabled = true;
    submitBtn.textContent = 'Zahlung wird vorbereitet...';

    const response = await createSubscription({ planId });

    if (!response?.clientSecret) {
      throw new Error('Client Secret fehlt in der Antwort');
    }

    // Initialize Stripe Elements
    const appearance = {
      theme: 'stripe',
      variables: {
        colorPrimary: '#0066ff',
        fontFamily: 'Inter, system-ui, sans-serif',
      },
    };

    const elements = stripe.elements({
      clientSecret: response.clientSecret,
      appearance,
    });

    const paymentElement = elements.create('payment');
    paymentElement.mount('#payment-element-container');

    submitBtn.disabled = false;
    submitBtn.textContent = 'Zahlungsmethode speichern';

    // Handle payment method setup submission
    const handleSubmit = async () => {
      hideError();
      submitBtn.disabled = true;
      submitBtn.textContent = 'Wird gespeichert...';

      try {
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          redirect: 'if_required',
          confirmParams: {
            return_url: window.location.href,
          },
        });

        if (error) {
          showError(error.message || 'Speichern fehlgeschlagen');
          submitBtn.disabled = false;
          submitBtn.textContent = 'Zahlungsmethode speichern';
        } else {
          if (setupIntent?.payment_method) {
            try {
              await updatePaymentMethod({ paymentMethodId: setupIntent.payment_method });
            } catch (updateError) {
              console.warn('Failed to update default payment method after setup', updateError);
              showToast('Zahlungsmethode gespeichert, Standard-Aktualisierung fehlgeschlagen.', 'warning');
            }
          }
          // Setup successful
          showToast('Zahlungsmethode gespeichert!', 'success');
          closeModal();

          // Refresh user data
          await refreshCurrentUser();

          if (onSuccess) {
            onSuccess();
          }
        }
      } catch (err) {
        console.error('Setup confirmation error:', err);
        showError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
        submitBtn.disabled = false;
        submitBtn.textContent = 'Zahlungsmethode speichern';
      }
    };

    submitBtn?.addEventListener('click', handleSubmit);

  } catch (error) {
    console.error('Payment modal error:', error);
    showError(error instanceof Error ? error.message : 'Fehler beim Laden des Zahlungsformulars');
    submitBtn.disabled = true;
  }
};

const buildReturnBaseUrl = () => {
  if (typeof config.checkoutReturnBaseUrl === 'string' && config.checkoutReturnBaseUrl.startsWith('http')) {
    return config.checkoutReturnBaseUrl.replace(/\/$/, '');
  }
  try {
    const url = new URL(window.location.href);
    url.hash = '';
    url.search = '';
    const sanitizedOrigin = url.origin.startsWith('http://') ? url.origin.replace('http://', 'https://') : url.origin;
    const path = url.pathname.replace(/index\.html$/i, '').replace(/\/$/, '');
    return `${sanitizedOrigin}${path}`;
  } catch (error) {
    console.warn('Failed to derive checkout return URL from window.location', error);
    return window.location.origin;
  }
};

const renderPlanCard = (plan, copy, { selected = false, buttonLabel = null, buttonAttrs = '' } = {}) => {
  if (!plan) {
    return `<div class="plan-card"><p class="muted">${copy.plan.unknown}</p></div>`;
  }
  return html`
    <div class="plan-card ${selected ? 'selected' : ''}">
      <div class="pill" style="align-self:flex-start;">${plan.name}</div>
      <span class="price">${plan.priceLabel}</span>
      <p class="muted" style="margin:0;">${plan.description}</p>
      ${buttonLabel
        ? `<button type="button" ${buttonAttrs}>${buttonLabel}</button>`
        : ''}
    </div>
  `;
};


const renderAlternativePlans = (currentPlanId, copy) => {
  const alternatives = PLANS.filter((plan) => plan.id !== currentPlanId && requiresBilling(plan));
  if (!alternatives.length) {
    return `<p class="muted small">${copy.plan.noAlternatives}</p>`;
  }
  return html`
    <div class="card" style="margin-top:12px;">
      <h4>${copy.plan.alternativesTitle}</h4>
      <div class="plan-grid">
        ${alternatives
          .map((plan) =>
            renderPlanCard(plan, copy, {
              buttonLabel: copy.plan.switchButton,
              buttonAttrs: `data-switch-plan="${plan.id}"`,
            }),
          )
          .join('')}
      </div>
    </div>
  `;
};

export const renderProfileView = (container) => {
  const copy = getProfileCopy();
  const commonCopy = getCommonCopy();
  const plansCopy = getPlansCopy();
  const user = getCurrentUser();
  if (!user) {
    container.innerHTML = `<div class="card"><p class="muted">${commonCopy.authRequired}</p></div>`;
    return;
  }

  const state = {
    planId: user.planId ?? DEFAULT_PLAN_ID,
    pendingPlanId: user.pendingPlanId ?? null,
    showPlanPicker: false,
    planStatus: '',
    activeTab: 'account',
    firstName: user.profile?.firstName ?? '',
    lastName: user.profile?.lastName ?? '',
    company: user.profile?.company ?? '',
    phone: user.profile?.phone ?? '',
    vatNumber: user.profile?.vatNumber ?? '',
    street: user.profile?.address?.street ?? '',
    postalCode: user.profile?.address?.postalCode ?? '',
    city: user.profile?.address?.city ?? '',
    country: user.profile?.address?.country ?? '',
    profileStatus: '',
    passwordStatus: '',
    releaseNotes: [],
    releaseNotesLoaded: false,
    releaseNotesLoading: false,
    releaseNotesError: '',
  };

  const tabLabels = copy.tabs ?? {
    account: copy.sectionTitles.account,
    personal: copy.sectionTitles.profile,
    plan: copy.sectionTitles.plan,
    export: 'Export Templates',
    billing: 'Billing',
    releaseNotes: 'Release Notes',
  };

  const buildCheckoutUrls = () => {
    const base = buildReturnBaseUrl();
    return {
      successUrl: `${base}#/profile?checkout=success`,
      cancelUrl: `${base}#/profile?checkout=cancel`,
    };
  };

  const startStripeCheckout = async (planId) => {
    const { successUrl, cancelUrl } = buildCheckoutUrls();
    try {
      const response = await createCheckoutSession({ planId, successUrl, cancelUrl });
      console.log('Profile checkout response:', response);
      if (!response?.checkoutUrl) {
        throw new Error('Stripe Checkout URL fehlt in Antwort.');
      }
      return response.checkoutUrl;
    } catch (error) {
      console.error('Profile checkout error:', error);
      throw error;
    }
  };

  const processPlanChange = async (planId, { statusEl, originButton = null, forceCheckout = false } = {}) => {
    if (!planId || !statusEl) {
      return;
    }
    const latestUser = getCurrentUser();
    if (!latestUser) {
      statusEl.textContent = commonCopy.authRequired;
      return;
    }
    if (planId === latestUser.planId && !latestUser.pendingPlanId && !forceCheckout) {
      statusEl.textContent = copy.statuses.planAlreadyActive;
      return;
    }
    const targetPlan = getPlanById(planId);
    if (!targetPlan) {
      statusEl.textContent = copy.statuses.unknownPlan;
      return;
    }

    const requiresCheckout = forceCheckout || (requiresBilling(targetPlan) && !hasActiveBilling(latestUser));
    statusEl.textContent = requiresCheckout ? copy.statuses.openingCheckout : copy.statuses.updatingPlan;
    state.planStatus = statusEl.textContent;
    if (originButton) {
      originButton.disabled = true;
    }

    try {
      if (requiresCheckout) {
        // Open inline payment modal instead of redirecting
        if (originButton) {
          originButton.disabled = false;
        }
        statusEl.textContent = '';
        state.planStatus = '';

        await showPaymentModal(planId, () => {
          // Success callback
          state.planId = planId;
          state.showPlanPicker = false;
          state.planStatus = copy.statuses.planUpdated;
          render();
        });
        return;
      }

      await updateUserPlan({
        email: latestUser.email,
        planId,
        clearBilling: !requiresBilling(targetPlan),
      });
      const refreshed = getCurrentUser();
      state.planId = refreshed?.planId ?? planId;
      state.showPlanPicker = false;
      state.planStatus = copy.statuses.planUpdated;
      render();
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message : copy.statuses.planFailed;
      state.planStatus = message;
      statusEl.textContent = message;
      if (originButton) {
        originButton.disabled = false;
      }
    }
  };

  const hashQuery = window.location.hash.split('?')[1] ?? '';
  if (hashQuery) {
    const params = new URLSearchParams(hashQuery);
    const checkoutStatus = params.get('checkout');
    const tabParam = params.get('tab');
    const actionParam = params.get('action');
    if (tabParam && ['account', 'personal', 'plan', 'export', 'billing', 'releaseNotes'].includes(tabParam)) {
      state.activeTab = tabParam;
    }
    if (actionParam === 'change-plan') {
      state.activeTab = 'plan';
      state.showPlanPicker = true;
      state.planStatus = '';
    }
    if (checkoutStatus === 'success') {
      state.planStatus = plansCopy.messages.checkoutSuccess;
      state.activeTab = 'plan';
    } else if (checkoutStatus === 'cancel') {
      state.planStatus = plansCopy.messages.checkoutCancel;
      state.activeTab = 'plan';
    }
    window.history.replaceState(null, '', `${window.location.origin}${window.location.pathname}#/profile`);
  }

  const syncProfileForm = (form) => {
    const data = new FormData(form);
    state.firstName = data.get('firstName')?.toString() ?? '';
    state.lastName = data.get('lastName')?.toString() ?? '';
    state.company = data.get('company')?.toString() ?? '';
    state.phone = data.get('phone')?.toString() ?? '';
    state.vatNumber = data.get('vatNumber')?.toString() ?? '';
    state.street = data.get('street')?.toString() ?? '';
    state.postalCode = data.get('postalCode')?.toString() ?? '';
    state.city = data.get('city')?.toString() ?? '';
    state.country = data.get('country')?.toString() ?? '';
  };

  const render = () => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      container.innerHTML = `<div class="card"><p class="muted">${commonCopy.authRequired}</p></div>`;
      return;
    }

    if (currentUser.planId && currentUser.planId !== state.planId) {
      state.planId = currentUser.planId;
    }

    const currentPlan = getPlanById(state.planId) ?? getPlanById(DEFAULT_PLAN_ID);
    const pendingPlanId = currentUser.pendingPlanId ?? null;
    state.pendingPlanId = pendingPlanId;
    const pendingPlan = pendingPlanId ? getPlanById(pendingPlanId) : null;

    const billingSummary = currentUser.billing
      ? `${currentUser.billing.brand?.toUpperCase() ?? copy.plan.billingFallback} •••• ${currentUser.billing.last4 ?? '????'}`
      : copy.plan.billingMissing;

    const billingActive = hasActiveBilling(currentUser);
    const needsBillingForCurrent = requiresBilling(currentPlan) && !billingActive;
    const checkoutTargetPlanId = pendingPlanId ?? (needsBillingForCurrent ? currentPlan.id : null);

    const checkoutNotice = (() => {
      if (pendingPlan && checkoutTargetPlanId) {
        return html`
          <div
            class="stack"
            style="gap:8px; border:1px solid rgba(249,115,22,0.35); padding:12px; border-radius:8px; background:rgba(249,115,22,0.08);"
          >
            <strong>${copy.plan.notices.pending.title}</strong>
            <p class="muted small">
              ${formatTemplate(copy.plan.notices.pending.description, { plan: pendingPlan.name ?? '' })}
            </p>
            <button type="button" data-start-checkout="${pendingPlan.id}">${copy.plan.notices.pending.action}</button>
          </div>
        `;
      }
      if (needsBillingForCurrent && checkoutTargetPlanId) {
        return html`
          <div
            class="stack"
            style="gap:8px; border:1px solid rgba(249,115,22,0.35); padding:12px; border-radius:8px; background:rgba(249,115,22,0.08);"
          >
            <strong>${copy.plan.notices.payment.title}</strong>
            <p class="muted small">
              ${formatTemplate(copy.plan.notices.payment.description, { plan: currentPlan.name ?? '' })}
            </p>
            <button type="button" data-start-checkout="${currentPlan.id}">${copy.plan.notices.payment.action}</button>
          </div>
        `;
      }
      return '';
    })();
    const planStatusMessage =
      state.planStatus || (pendingPlan ? formatTemplate(copy.plan.pendingLabel, { plan: pendingPlan.name ?? '' }) : '');

    const renderTabContent = () => {
      switch (state.activeTab) {
        case 'account':
          return html`
            <div class="card stack">
              <section class="stack" style="gap:16px;">
                <div>
                  <h3>${copy.sectionTitles.account}</h3>
                  <div class="muted small">${copy.labels.email}</div>
                  <strong>${currentUser.email}</strong>
                </div>
                <form class="stack" id="password-form">
                  <h3>${copy.sectionTitles.password}</h3>
                  <label>
                    ${copy.labels.currentPassword}
                    <input type="password" name="currentPassword" required autocomplete="current-password" />
                  </label>
                  <label>
                    ${copy.labels.newPassword}
                    <input type="password" name="newPassword" required minlength="6" autocomplete="new-password" />
                  </label>
                  <label>
                    ${copy.labels.confirmPassword}
                    <input type="password" name="confirmPassword" required minlength="6" autocomplete="new-password" />
                  </label>
                  <button type="submit">${copy.labels.passwordButton}</button>
                  <p class="muted" id="password-status">${state.passwordStatus}</p>
                </form>
                <section class="stack" style="gap:8px;">
                  <button
                    type="button"
                    id="delete-account-button"
                    class="ghost-button"
                    style="align-self:flex-start; color:#f87171; border-color:rgba(248,113,113,0.6);"
                  >
                    ${copy.labels.deleteButton}
                  </button>
                  <p class="muted small" id="delete-status"></p>
                </section>
              </section>
            </div>
          `;
        case 'personal':
          return html`
            <div class="card stack">
              <form class="stack" id="profile-form">
                <h3>${copy.sectionTitles.profile}</h3>
                <p class="muted small">${copy.labels.requiredNote}</p>
                <div class="row">
                  <label style="flex:1;">
                    ${copy.labels.firstName}
                    <input type="text" name="firstName" value="${state.firstName}" required autocomplete="given-name" />
                  </label>
                  <label style="flex:1;">
                    ${copy.labels.lastName}
                    <input type="text" name="lastName" value="${state.lastName}" required autocomplete="family-name" />
                  </label>
                </div>
                <label>
                  ${copy.labels.company}
                  <input type="text" name="company" value="${state.company}" autocomplete="organization" />
                </label>
                <label>
                  ${copy.labels.phone}
                  <input type="tel" name="phone" value="${state.phone}" required autocomplete="tel" />
                </label>
                <label>
                  ${copy.labels.vatNumber}
                  <input type="text" name="vatNumber" value="${state.vatNumber}" placeholder="DE123456789" autocomplete="off" />
                </label>
                <div class="row">
                  <label style="flex:1;">
                    ${copy.labels.street}
                    <input type="text" name="street" value="${state.street}" required autocomplete="address-line1" />
                  </label>
                  <label style="flex:0.5;">
                    ${copy.labels.postalCode}
                    <input type="text" name="postalCode" value="${state.postalCode}" required autocomplete="postal-code" />
                  </label>
                  <label style="flex:1;">
                    ${copy.labels.city}
                    <input type="text" name="city" value="${state.city}" required autocomplete="address-level2" />
                  </label>
                </div>
                <label>
                  ${copy.labels.country}
                  <input type="text" name="country" value="${state.country}" required autocomplete="country-name" />
                </label>
                <button type="submit">${copy.labels.profileButton}</button>
                <p class="muted" id="profile-status">${state.profileStatus}</p>
              </form>
            </div>
          `;
        case 'plan':
          return html`
            <div class="card stack">
              <div class="stack" id="plan-section" style="gap:16px;">
                <h3>${copy.sectionTitles.plan}</h3>
                <div id="current-plan" class="plan-grid">
                  ${renderPlanCard(currentPlan, copy, {
                    selected: true,
                    buttonLabel: copy.plan.currentBadge,
                    buttonAttrs: 'disabled',
                  })}
                </div>
                <p class="muted small">${copy.labels.billing} ${billingSummary}</p>
                <div class="row" style="gap:12px; flex-wrap: wrap;">
                  <button type="button" id="toggle-plan-picker">${copy.plan.togglePicker}</button>
                </div>
                <p class="muted" id="plan-status">${planStatusMessage}</p>
                ${checkoutNotice}
                ${state.showPlanPicker ? renderAlternativePlans(state.planId, copy) : ''}
              </div>
            </div>
          `;
        case 'export':
          return html`<div id="profile-export-templates"></div>`;
        case 'billing':
          return html`<div id="profile-billing"></div>`;
        case 'releaseNotes': {
          const releaseCopy = copy.releaseNotes ?? {};
          const language = getLanguage();
          const preparedReleaseNotes = prepareReleaseNotes(state.releaseNotes, language);
          if (state.releaseNotesLoading && !state.releaseNotesLoaded) {
            return html`
              <div class="card stack" id="profile-release-notes">
                <h3>${releaseCopy.title ?? 'Release Notes'}</h3>
                ${releaseCopy.subtitle ? `<p class="muted small">${releaseCopy.subtitle}</p>` : ''}
                <p class="muted">${releaseCopy.loading ?? 'Lade Release Notes…'}</p>
              </div>
            `;
          }
          if (state.releaseNotesError) {
            return html`
              <div class="card stack" id="profile-release-notes">
                <h3>${releaseCopy.title ?? 'Release Notes'}</h3>
                ${releaseCopy.subtitle ? `<p class="muted small">${releaseCopy.subtitle}</p>` : ''}
                <p class="muted">${state.releaseNotesError}</p>
              </div>
            `;
          }
          if (!preparedReleaseNotes.length) {
            return html`
              <div class="card stack" id="profile-release-notes">
                <h3>${releaseCopy.title ?? 'Release Notes'}</h3>
                ${releaseCopy.subtitle ? `<p class="muted small">${releaseCopy.subtitle}</p>` : ''}
                <p class="muted">${releaseCopy.empty ?? 'Keine Release Notes vorhanden.'}</p>
              </div>
            `;
          }
          return html`
            <div class="card stack" id="profile-release-notes">
              <h3>${releaseCopy.title ?? 'Release Notes'}</h3>
              ${releaseCopy.subtitle ? `<p class="muted small">${releaseCopy.subtitle}</p>` : ''}
              <div class="release-notes-content">
                ${renderReleaseNotesList(preparedReleaseNotes, language, copy)}
              </div>
            </div>
          `;
        }
        default:
          return '';
      }
    };

    container.innerHTML = html`
      <div class="profile-view stack">
        <div class="view-header">
          <h1>${copy.title}</h1>
        </div>
        <div class="tabs">
          <button class="tab ${state.activeTab === 'account' ? 'active' : ''}" data-tab="account">
            ${tabLabels.account}
          </button>
          <button class="tab ${state.activeTab === 'personal' ? 'active' : ''}" data-tab="personal">
            ${tabLabels.personal}
          </button>
          <button class="tab ${state.activeTab === 'plan' ? 'active' : ''}" data-tab="plan">
            ${tabLabels.plan}
          </button>
          <button class="tab ${state.activeTab === 'export' ? 'active' : ''}" data-tab="export">
            ${tabLabels.export}
          </button>
          <button class="tab ${state.activeTab === 'billing' ? 'active' : ''}" data-tab="billing">
            ${tabLabels.billing}
          </button>
          <button class="tab ${state.activeTab === 'releaseNotes' ? 'active' : ''}" data-tab="releaseNotes">
            ${tabLabels.releaseNotes}
          </button>
        </div>
        <div class="tab-content">
          ${renderTabContent()}
        </div>
      </div>
    `;

    container.querySelectorAll('[data-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        state.activeTab = tab.dataset.tab;
        render();
      });
    });

    const planStatus = container.querySelector('#plan-status');
    const togglePlanPicker = container.querySelector('#toggle-plan-picker');
    const planSection = container.querySelector('#plan-section');

    if (togglePlanPicker) {
      togglePlanPicker.addEventListener('click', () => {
        state.showPlanPicker = !state.showPlanPicker;
        state.planStatus = '';
        render();
      });
    }

    if (planSection && planStatus) {
      planSection.querySelectorAll('[data-switch-plan]').forEach((button) => {
        button.addEventListener('click', async (event) => {
          const newPlanId = event.currentTarget.getAttribute('data-switch-plan');
          if (!newPlanId) {
            return;
          }
          await processPlanChange(newPlanId, { statusEl: planStatus, originButton: event.currentTarget });
        });
      });
      planSection.querySelectorAll('[data-start-checkout]').forEach((button) => {
        button.addEventListener('click', async (event) => {
          const targetPlanId = event.currentTarget.getAttribute('data-start-checkout');
          if (!targetPlanId) {
            return;
          }
          await processPlanChange(targetPlanId, { statusEl: planStatus, originButton: event.currentTarget, forceCheckout: true });
        });
      });
    }

    const profileForm = container.querySelector('#profile-form');
    const profileStatus = container.querySelector('#profile-status');

    if (profileForm && profileStatus) {
      profileForm.addEventListener('input', () => {
        syncProfileForm(profileForm);
      });

      profileForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        syncProfileForm(profileForm);
        profileStatus.textContent = copy.statuses.profileSaving;
        profileForm.querySelectorAll('button').forEach((btn) => {
          btn.disabled = true;
        });
        try {
          await updateUserProfile({
            email: user.email,
            firstName: state.firstName,
            lastName: state.lastName,
            company: state.company,
            phone: state.phone,
            vatNumber: state.vatNumber,
            street: state.street,
            postalCode: state.postalCode,
            city: state.city,
            country: state.country,
          });
          const refreshed = getCurrentUser();
          state.firstName = refreshed?.profile?.firstName ?? state.firstName;
          state.lastName = refreshed?.profile?.lastName ?? state.lastName;
          state.company = refreshed?.profile?.company ?? state.company;
          state.phone = refreshed?.profile?.phone ?? state.phone;
          state.vatNumber = refreshed?.profile?.vatNumber ?? state.vatNumber;
          state.street = refreshed?.profile?.address?.street ?? state.street;
          state.postalCode = refreshed?.profile?.address?.postalCode ?? state.postalCode;
          state.city = refreshed?.profile?.address?.city ?? state.city;
          state.country = refreshed?.profile?.address?.country ?? state.country;
          state.profileStatus = copy.statuses.profileSaved;
          profileStatus.textContent = state.profileStatus;
        } catch (error) {
          console.error(error);
          state.profileStatus = error instanceof Error ? error.message : copy.statuses.profileError;
          profileStatus.textContent = state.profileStatus;
        } finally {
          profileForm.querySelectorAll('button').forEach((btn) => {
            btn.disabled = false;
          });
        }
      });
    }
    const passwordForm = container.querySelector('#password-form');
    const passwordStatus = container.querySelector('#password-status');

    if (passwordForm && passwordStatus) {
      passwordForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const data = new FormData(passwordForm);
        const currentPassword = data.get('currentPassword')?.toString() ?? '';
        const newPassword = data.get('newPassword')?.toString() ?? '';
        const confirmPassword = data.get('confirmPassword')?.toString() ?? '';

        if (!currentPassword) {
          const message = copy.statuses.passwordMissingCurrent;
          state.passwordStatus = message;
          passwordStatus.textContent = message;
          return;
        }

        if (!newPassword || newPassword.length < 6) {
          const message = copy.statuses.passwordTooShort;
          state.passwordStatus = message;
          passwordStatus.textContent = message;
          return;
        }

        if (newPassword !== confirmPassword) {
          const message = copy.statuses.passwordMismatch;
          state.passwordStatus = message;
          passwordStatus.textContent = message;
          return;
        }

        state.passwordStatus = copy.statuses.passwordUpdating;
        passwordStatus.textContent = state.passwordStatus;
        passwordForm.querySelectorAll('button').forEach((btn) => {
          btn.disabled = true;
        });

        try {
          await updateUserPassword({
            email: currentUser.email,
            password: newPassword,
            currentPassword,
          });
          state.passwordStatus = copy.statuses.passwordUpdated;
          passwordStatus.textContent = state.passwordStatus;
          passwordForm.reset();
        } catch (error) {
          console.error(error);
          const message = error instanceof Error ? error.message : copy.statuses.passwordError;
          state.passwordStatus = message;
          passwordStatus.textContent = state.passwordStatus;
        } finally {
          passwordForm.querySelectorAll('button').forEach((btn) => {
            btn.disabled = false;
          });
        }
      });
    }

    const deleteButton = container.querySelector('#delete-account-button');
    const deleteStatus = container.querySelector('#delete-status');
    if (deleteButton && deleteStatus) {
      deleteButton.addEventListener('click', async () => {
        if (!window.confirm(copy.statuses.deleteConfirm)) {
          return;
        }
        deleteStatus.textContent = copy.statuses.deleteInProgress;
        deleteButton.disabled = true;
        try {
          await deleteUser(currentUser.email);
          deleteStatus.textContent = copy.statuses.deleteSuccess;
          await logout();
          window.location.hash = '#/login';
        } catch (error) {
          console.error(error);
          deleteStatus.textContent = error instanceof Error ? error.message : copy.statuses.deleteFailed;
          deleteButton.disabled = false;
        }
      });
    }

    if (state.activeTab === 'export') {
      const exportContainer = container.querySelector('#profile-export-templates');
      if (exportContainer) {
        renderExportTemplatesView(exportContainer);
      }
    }

    if (state.activeTab === 'billing') {
      const billingContainer = container.querySelector('#profile-billing');
      if (billingContainer) {
        renderBillingView(billingContainer);
      }
    }

    if (state.activeTab === 'releaseNotes' && !state.releaseNotesLoaded && !state.releaseNotesLoading) {
      state.releaseNotesLoading = true;
      const language = getLanguage();
      getReleaseNotes({ language, all: true })
        .then((data) => {
          state.releaseNotes = Array.isArray(data?.releases) ? data.releases : [];
          state.releaseNotesLoaded = true;
          state.releaseNotesError = '';
        })
        .catch((error) => {
          console.error('Failed to load release notes', error);
          state.releaseNotesError =
            error instanceof Error ? error.message : copy.releaseNotes?.loadError ?? 'Release Notes konnten nicht geladen werden.';
          state.releaseNotesLoaded = true;
        })
        .finally(() => {
          state.releaseNotesLoading = false;
          render();
        });
    }
  };

  render();
};
